import { forwardRef, HTMLAttributes, ReactNode, useState } from 'react'
import { SCloseButton, SHeader, STitle, SPopup, SArrowPositioner, SArrow } from './Popup.styled'
import { useLoadingCallback } from 'utils/hooks'
import { StateSpinner } from 'components/StateSpinner'
import { usePopper } from 'react-popper'
import immer from 'immer'
import React from 'react'
import { Icon } from 'components/icon'
interface PopupConfig {
  onClose: () => Promise<void>
  title?: string
  show?: boolean
  popperConfig?: Parameters<typeof usePopper>[2]
}

interface PopupProps extends PopupConfig {
  popper: ReturnType<typeof usePopper>
}

const defaultModifiers = {
  offset: {
    options: {
      offset: [0, 24],
    },
  },
  preventOverflow: {
    options: {
      padding: 16,
    },
  },
}

const Popup = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement> & PopupProps>(
  ({ popper, show, onClose, title, children, ...props }, ref) => {
    const [close, isClosing] = useLoadingCallback(onClose)
    return (
      <>
        <SPopup {...props} style={popper.styles.popper} show={show} ref={ref}>
          <SArrowPositioner data-popper-arrow style={popper.styles.arrow}>
            <SArrow />
          </SArrowPositioner>
          <SHeader>
            <STitle>{title}</STitle>
            <StateSpinner isLoading={isClosing}>
              <SCloseButton type="button" variant="text" onClick={close}>
                <Icon name="Close" size={12} color="var(--c-gray500)" />
              </SCloseButton>
            </StateSpinner>
          </SHeader>
          {children}
        </SPopup>
      </>
    )
  }
)
Popup.displayName = 'Popup'

export const usePopup = (render: ReactNode, config: PopupConfig) => {
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)

  const popper = usePopper(
    referenceElement,
    popperElement,
    immer(config.popperConfig, (popperConfig) => {
      popperConfig?.modifiers?.push({
        name: 'arrow',
        options: {
          offset: 0,
        },
      })
      for (const [name, { options }] of Object.entries(defaultModifiers)) {
        const modifier = popperConfig?.modifiers?.find((mod) => mod.name === name)
        if (!modifier) {
          // if not specified, add the default modifier
          if (popperConfig && !popperConfig?.modifiers) {
            popperConfig.modifiers = []
          }
          popperConfig?.modifiers?.push({
            name,
            options,
          })
        }
      }
    })
  )

  return {
    popup: (
      <Popup {...config} popper={popper} ref={setPopperElement}>
        {render}
      </Popup>
    ),
    setReferenceElement,
  }
}
